/* eslint-disable no-nested-ternary */
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiMaximize,
  FiMinimize,
  FiRefreshCw,
  FiUser,
} from 'react-icons/fi';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  ExpirationItem,
  LossControlIndicators,
  LossControlSummary,
  lossControlService,
} from '../../../services/lossControlService';
import { parseLossControlDisplayMode } from './displayMode';
import {
  formatCuiabaDate,
  formatCuiabaTime,
  formatCuiabaTimestamp,
  formatExpirationDate,
  getExpirationPresentation,
  paginateExpirationItems,
  prioritizeExpirationItems,
  shouldBlinkExpiration,
  shouldBlinkIndicators,
} from './utils';

interface DisplayParams {
  branchId: string;
}

interface DisplayRow {
  id: string;
  name: string;
  type: 'operator' | 'unassigned';
  displayOrder: number;
  indicators: LossControlIndicators;
}

const OPERATOR_ITEMS_PER_PAGE = 6;
const PRODUCT_ITEMS_PER_PAGE = 5;
const PAGE_INTERVAL_MS = 8000;

const LossControlDisplay: React.FC = () => {
  const { branchId } = useParams<DisplayParams>();
  const location = useLocation();
  const history = useHistory();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const demo = query.get('demo') === '1';
  const displayMode = parseLossControlDisplayMode(query.get('mode'));
  const urlToken = useMemo(() => {
    const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
    return hash.get('token') || query.get('token') || '';
  }, [location.hash, query]);
  const tokenStorageKey = `@Samasc:loss-control-display-token:${branchId}`;
  const [token, setToken] = useState(
    () => urlToken || localStorage.getItem(tokenStorageKey) || '',
  );
  const [summary, setSummary] = useState<LossControlSummary | null>(null);
  const [items, setItems] = useState<ExpirationItem[]>([]);
  const [productUpdatedAt, setProductUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(new Date());
  const [requestedPage, setRequestedPage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(
    Boolean(document.fullscreenElement),
  );

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!urlToken) {
      setToken(localStorage.getItem(tokenStorageKey) || '');
    }
  }, [tokenStorageKey, urlToken]);

  useEffect(() => {
    if (!urlToken) return;

    localStorage.setItem(tokenStorageKey, urlToken);
    setToken(urlToken);

    const sanitizedQuery = new URLSearchParams(location.search);
    sanitizedQuery.delete('token');
    history.replace({
      pathname: location.pathname,
      search: sanitizedQuery.toString(),
      hash: '',
    });
  }, [history, location.pathname, location.search, tokenStorageKey, urlToken]);

  const loadData = useCallback(async () => {
    if (!demo && !token) {
      setError(
        'Painel não configurado. Gere o acesso da TV na página do Controle de Perdas.',
      );
      setLoading(false);
      return;
    }

    try {
      if (displayMode === 'products') {
        const response = await lossControlService.getDisplay(
          branchId,
          token,
          demo,
        );
        setItems(response.items);
        setProductUpdatedAt(
          response.sourceUpdatedAt || response.generatedAt || null,
        );
        setError(
          response.sourceAvailable
            ? ''
            : 'GesCooper offline: exibindo o último dado sincronizado.',
        );
      } else {
        const response = await lossControlService.getDisplaySummary(
          branchId,
          token,
          demo,
        );
        setSummary(response);
        setError('');
      }
    } catch (requestError) {
      setError(
        displayMode === 'products'
          ? 'Não foi possível atualizar o painel. A última lista válida foi mantida.'
          : 'Não foi possível atualizar o painel. O último resumo válido foi mantido.',
      );
    } finally {
      setLoading(false);
    }
  }, [branchId, demo, displayMode, token]);

  useEffect(() => {
    setLoading(true);
    setRequestedPage(0);
    setError('');
  }, [branchId, displayMode]);

  useEffect(() => {
    loadData();
    const dataTimer = window.setInterval(loadData, 60000);
    const clockTimer = window.setInterval(() => setNow(new Date()), 1000);

    return () => {
      window.clearInterval(dataTimer);
      window.clearInterval(clockTimer);
    };
  }, [loadData]);

  const rows = useMemo<DisplayRow[]>(() => {
    if (!summary) return [];

    const operatorRows: DisplayRow[] = summary.operators.map(operator => ({
      id: operator.accessId,
      name: operator.name,
      type: 'operator',
      displayOrder: operator.displayOrder,
      indicators: operator.indicators,
    }));

    if (summary.unassigned.visible) {
      operatorRows.push({
        id: 'unassigned',
        name: summary.unassigned.label,
        type: 'unassigned',
        displayOrder: summary.unassigned.displayOrder,
        indicators: summary.unassigned.indicators,
      });
    }

    return operatorRows.sort(
      (first, second) =>
        first.displayOrder - second.displayOrder ||
        first.name.localeCompare(second.name),
    );
  }, [summary]);

  const prioritizedItems = useMemo(
    () => prioritizeExpirationItems(items, items.length),
    [items],
  );
  const expirationPage = useMemo(
    () =>
      paginateExpirationItems(
        prioritizedItems,
        requestedPage,
        PRODUCT_ITEMS_PER_PAGE,
      ),
    [prioritizedItems, requestedPage],
  );
  const operatorTotalPages = Math.max(
    1,
    Math.ceil(rows.length / OPERATOR_ITEMS_PER_PAGE),
  );
  const operatorCurrentPage =
    ((requestedPage % operatorTotalPages) + operatorTotalPages) %
    operatorTotalPages;
  const visibleRows = rows.slice(
    operatorCurrentPage * OPERATOR_ITEMS_PER_PAGE,
    operatorCurrentPage * OPERATOR_ITEMS_PER_PAGE + OPERATOR_ITEMS_PER_PAGE,
  );
  const totalPages =
    displayMode === 'products' ? expirationPage.totalPages : operatorTotalPages;
  const currentPage =
    displayMode === 'products'
      ? expirationPage.currentPage
      : operatorCurrentPage;

  useEffect(() => {
    setRequestedPage(0);
  }, [branchId, displayMode, items.length, rows.length]);

  useEffect(() => {
    if (totalPages <= 1) return undefined;

    const pageTimer = window.setInterval(() => {
      setRequestedPage(page => page + 1);
    }, PAGE_INTERVAL_MS);

    return () => window.clearInterval(pageTimer);
  }, [totalPages]);

  const colors = {
    page: '#F1F5F9',
    text: '#0F172A',
    muted: '#475569',
    table: '#FFFFFF',
    tableBorder: '#CBD5E1',
    tableHeader: '#E2E8F0',
    rowBorder: '#CBD5E1',
    number: '#E2E8F0',
    buttonBorder: '#94A3B8',
    buttonHover: '#E2E8F0',
    error: '#FFEDD5',
    errorBorder: '#F97316',
    errorText: '#7C2D12',
    empty: '#166534',
    operatorIcon: '#DCFCE7',
    unassignedIcon: '#FFEDD5',
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }

      return;
    }

    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  };

  const branchName =
    displayMode === 'products'
      ? items[0]?.branchName || `Loja ${branchId}`
      : summary?.branchName || `Loja ${branchId}`;
  const updatedAt =
    displayMode === 'products'
      ? productUpdatedAt
      : summary?.sourceUpdatedAt || summary?.generatedAt || null;

  return (
    <Box
      h="100vh"
      w="100vw"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      bg={colors.page}
      color={colors.text}
      px={{ base: 4, lg: 8 }}
      py={{ base: 3, lg: 4 }}
    >
      <Flex
        align="center"
        justify="space-between"
        mb={{ base: 3, lg: 4 }}
        gridGap="5"
        flexShrink={0}
      >
        <Flex align="center" gridGap="4">
          <Flex
            w={{ base: '14', lg: '16' }}
            h={{ base: '14', lg: '16' }}
            borderRadius="xl"
            align="center"
            justify="center"
            bg="orange.400"
          >
            <Icon
              as={FiAlertTriangle}
              boxSize={{ base: '8', lg: '10' }}
              color="#0F172A"
            />
          </Flex>
          <Box>
            <Heading fontSize={{ base: '2xl', lg: '5xl' }}>
              Controle de Perdas
            </Heading>
            <Text color={colors.muted} fontSize={{ base: 'md', lg: '2xl' }}>
              {branchName} ·{' '}
              {displayMode === 'products'
                ? `${items.length} lotes por ordem de vencimento`
                : `${summary?.totalUniqueLots || 0} lotes monitorados`}
            </Text>
          </Box>
        </Flex>

        <Flex align="center" gridGap="5">
          <Badge
            colorScheme={displayMode === 'products' ? 'blue' : 'green'}
            fontSize="md"
          >
            {displayMode === 'products' ? 'Por produto' : 'Por operador'}
          </Badge>
          {demo && (
            <Badge colorScheme="purple" fontSize="md">
              Demonstração
            </Badge>
          )}
          <Box textAlign="right" display={{ base: 'none', md: 'block' }}>
            <Text fontSize="2xl" fontWeight="bold">
              {formatCuiabaTime(now)}
            </Text>
            <Text color={colors.muted}>{formatCuiabaDate(now)}</Text>
          </Box>
          <Button
            variant="outline"
            borderColor={colors.buttonBorder}
            color={colors.text}
            _hover={{ bg: colors.buttonHover }}
            leftIcon={isFullscreen ? <FiMinimize /> : <FiMaximize />}
            onClick={toggleFullscreen}
          >
            {isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          </Button>
        </Flex>
      </Flex>

      {error && (
        <Flex
          bg={colors.error}
          border="1px solid"
          borderColor={colors.errorBorder}
          color={colors.errorText}
          px="5"
          py="3"
          mb="4"
          borderRadius="lg"
          align="center"
        >
          <Icon as={FiRefreshCw} mr="3" />
          <Text>{error}</Text>
        </Flex>
      )}

      <Box
        flex="1"
        minH="0"
        display="flex"
        bg={colors.table}
        border="1px solid"
        borderColor={colors.tableBorder}
        borderRadius="xl"
        overflow="hidden"
        sx={{
          '@keyframes urgentBlink': {
            '0%, 100%': {
              backgroundColor: 'rgba(197, 48, 48, 0.25)',
              boxShadow: 'inset 0 0 0 0 #E53E3E',
            },
            '50%': {
              backgroundColor: 'rgba(229, 62, 62, 0.62)',
              boxShadow: 'inset 0 0 28px 2px #FC8181',
            },
          },
        }}
      >
        {loading ? (
          <Flex flex="1" align="center" justify="center">
            <Spinner size="xl" color="orange.400" />
          </Flex>
        ) : displayMode === 'products' ? (
          expirationPage.items.length === 0 ? (
            <Flex flex="1" align="center" justify="center" direction="column">
              <Heading size="lg" color={colors.empty}>
                Nenhum lote monitorado
              </Heading>
              <Text mt="2" color={colors.muted}>
                Não há produtos com validade cadastrada para esta loja.
              </Text>
            </Flex>
          ) : (
            <Table variant="unstyled" size="lg" h="100%" tableLayout="fixed">
              <Thead bg={colors.tableHeader}>
                <Tr>
                  <Th
                    w="23%"
                    color={colors.muted}
                    fontSize={{ base: 'md', lg: '2xl' }}
                    py={{ base: '3', lg: '4' }}
                    title="Prazo em ordem crescente"
                  >
                    Prazo ↑
                  </Th>
                  <Th
                    w="31%"
                    color={colors.muted}
                    fontSize={{ base: 'md', lg: '2xl' }}
                    py={{ base: '3', lg: '4' }}
                  >
                    Produtos
                  </Th>
                  <Th
                    w="16%"
                    color={colors.muted}
                    fontSize={{ base: 'md', lg: '2xl' }}
                    py={{ base: '3', lg: '4' }}
                  >
                    Lote
                  </Th>
                  <Th
                    w="13%"
                    color={colors.muted}
                    fontSize={{ base: 'md', lg: '2xl' }}
                    py={{ base: '3', lg: '4' }}
                    textAlign="center"
                  >
                    Quantidade
                  </Th>
                  <Th
                    w="17%"
                    color={colors.muted}
                    fontSize={{ base: 'md', lg: '2xl' }}
                    py={{ base: '3', lg: '4' }}
                  >
                    Validade
                  </Th>
                </Tr>
              </Thead>
              <Tbody
                key={expirationPage.currentPage}
                animation="pageEnter 0.45s ease-out"
                sx={{
                  '@keyframes pageEnter': {
                    from: { opacity: 0, transform: 'translateY(18px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                {expirationPage.items.map((item, index) => {
                  const presentation = getExpirationPresentation(item.days);
                  const shouldBlink = shouldBlinkExpiration(item.days);
                  const rowNumber =
                    expirationPage.currentPage * PRODUCT_ITEMS_PER_PAGE +
                    index +
                    1;

                  return (
                    <Tr
                      key={item.id}
                      borderBottom="1px solid"
                      borderBottomColor={colors.rowBorder}
                      bg={`${presentation.accent}33`}
                      animation={
                        shouldBlink
                          ? 'urgentBlink 1.35s ease-in-out infinite'
                          : ''
                      }
                      sx={{
                        '@media (prefers-reduced-motion: reduce)': {
                          animation: 'none',
                        },
                      }}
                    >
                      <Td
                        borderLeft="8px solid"
                        borderLeftColor={presentation.accent}
                        py={{ base: '3', lg: '3' }}
                      >
                        <Flex align="center" gridGap={{ base: '2', lg: '4' }}>
                          <Flex
                            flexShrink={0}
                            w={{ base: '8', lg: '12' }}
                            h={{ base: '8', lg: '12' }}
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg={colors.number}
                            color={colors.text}
                            fontSize={{ base: 'sm', lg: 'xl' }}
                            fontWeight="bold"
                          >
                            {String(rowNumber).padStart(2, '0')}
                          </Flex>
                          <Badge
                            colorScheme={presentation.colorScheme}
                            fontSize={{ base: 'md', lg: '2xl' }}
                            px={{ base: '3', lg: '5' }}
                            py={{ base: '1', lg: '2' }}
                            whiteSpace="nowrap"
                          >
                            {presentation.label}
                          </Badge>
                        </Flex>
                      </Td>
                      <Td>
                        <Text
                          fontWeight="bold"
                          fontSize={{ base: 'xl', lg: '3xl' }}
                          lineHeight="shorter"
                        >
                          {item.productName}
                        </Text>
                        <Text
                          color={colors.muted}
                          fontSize={{ base: 'sm', lg: '2xl' }}
                        >
                          Cód. {item.productCode}
                        </Text>
                      </Td>
                      <Td
                        fontSize={{ base: 'xl', lg: '3xl' }}
                        fontWeight="semibold"
                      >
                        {item.lot || '-'}
                      </Td>
                      <Td
                        fontSize={{ base: 'xl', lg: '3xl' }}
                        fontWeight="bold"
                        textAlign="center"
                        whiteSpace="nowrap"
                      >
                        {item.quantity.toLocaleString('pt-BR')}{' '}
                        <Text as="span" fontSize={{ base: 'sm', lg: 'xl' }}>
                          {item.unit || ''}
                        </Text>
                      </Td>
                      <Td
                        fontSize={{ base: 'xl', lg: '3xl' }}
                        fontWeight="bold"
                        whiteSpace="nowrap"
                      >
                        {formatExpirationDate(item.expiryDate)}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )
        ) : visibleRows.length === 0 ? (
          <Flex flex="1" align="center" justify="center" direction="column">
            <Heading size="lg" color={colors.empty}>
              Nenhum responsável configurado
            </Heading>
            <Text mt="2" color={colors.muted}>
              Cadastre as responsabilidades dos operadores no SAMASC.
            </Text>
          </Flex>
        ) : (
          <Table variant="unstyled" size="lg" h="100%" tableLayout="fixed">
            <Thead bg={colors.tableHeader}>
              <Tr>
                <Th w="40%" color={colors.muted} fontSize="2xl" py="4">
                  Responsável
                </Th>
                <Th color={colors.muted} fontSize="2xl" isNumeric>
                  Vencidos
                </Th>
                <Th color={colors.muted} fontSize="2xl" isNumeric>
                  Hoje
                </Th>
                <Th color={colors.muted} fontSize="2xl" isNumeric>
                  1 a 7 dias
                </Th>
                <Th color={colors.muted} fontSize="2xl" isNumeric>
                  8 a 30 dias
                </Th>
                <Th color={colors.muted} fontSize="2xl" isNumeric>
                  Total
                </Th>
              </Tr>
            </Thead>
            <Tbody
              key={currentPage}
              animation="pageEnter 0.45s ease-out"
              sx={{
                '@keyframes pageEnter': {
                  from: { opacity: 0, transform: 'translateY(18px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              {visibleRows.map(row => {
                const shouldBlink = shouldBlinkIndicators(row.indicators);
                return (
                  <Tr
                    key={row.id}
                    borderBottom="1px solid"
                    borderBottomColor={colors.rowBorder}
                    bg={shouldBlink ? 'rgba(229, 62, 62, 0.13)' : undefined}
                    animation={
                      shouldBlink
                        ? 'urgentBlink 1.35s ease-in-out infinite'
                        : ''
                    }
                    sx={{
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                      },
                    }}
                  >
                    <Td
                      borderLeft="8px solid"
                      borderLeftColor={
                        row.type === 'unassigned' ? 'orange.400' : 'green.400'
                      }
                    >
                      <Flex align="center" gridGap="5">
                        <Flex
                          w="14"
                          h="14"
                          flexShrink={0}
                          borderRadius="full"
                          align="center"
                          justify="center"
                          bg={
                            row.type === 'unassigned'
                              ? colors.unassignedIcon
                              : colors.operatorIcon
                          }
                        >
                          <Icon
                            as={
                              row.type === 'unassigned'
                                ? FiAlertTriangle
                                : FiUser
                            }
                            boxSize="7"
                            color={
                              row.type === 'unassigned'
                                ? 'orange.300'
                                : 'green.300'
                            }
                          />
                        </Flex>
                        <Box>
                          <Text fontSize="3xl" fontWeight="bold">
                            {row.name}
                          </Text>
                          <Text color={colors.muted} fontSize="lg">
                            {row.type === 'unassigned'
                              ? 'Pendência de responsabilidade'
                              : 'Operador responsável'}
                          </Text>
                        </Box>
                      </Flex>
                    </Td>
                    <Td fontSize="3xl" fontWeight="bold" isNumeric>
                      {row.indicators.expired}
                    </Td>
                    <Td fontSize="3xl" fontWeight="bold" isNumeric>
                      {row.indicators.dueToday}
                    </Td>
                    <Td fontSize="3xl" fontWeight="bold" isNumeric>
                      {row.indicators.dueIn1To7Days}
                    </Td>
                    <Td fontSize="3xl" fontWeight="bold" isNumeric>
                      {row.indicators.dueIn8To15Days +
                        row.indicators.dueIn16To30Days}
                    </Td>
                    <Td fontSize="4xl" fontWeight="bold" isNumeric>
                      {row.indicators.total}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Box>

      <Flex
        justify="space-between"
        mt={{ base: '2', lg: '3' }}
        color={colors.muted}
        fontSize={{ base: 'xs', lg: 'md' }}
        flexShrink={0}
      >
        <Text>
          {displayMode === 'products' ? 'Dados' : 'Atualização'}: 60s ·
          Exibição: 8s · Página {currentPage + 1} de {totalPages}
        </Text>
        <Text>
          Dados atualizados em{' '}
          {updatedAt ? formatCuiabaTimestamp(updatedAt) : '-'}
        </Text>
      </Flex>
    </Box>
  );
};

export default LossControlDisplay;
